import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { auth, db } from '../../services/firebase';
import {
  buildTrackerEvidencePayload,
  buildTrackerPayload,
  TRACKER_EVIDENCE_COLLECTION,
  TRACKER_SOURCE_TYPES,
  TRACKER_STATUSES,
  TRACKERS_COLLECTION,
} from './trackerModel';
import {
  findTrackerCandidateMatches,
  getBestTrackerCandidateMatch,
} from './trackerMatching';

const getCurrentUserId = () => auth.currentUser?.uid || '';

const DAILY_LOGS_COLLECTION = 'dailyLogs';

const normalizeText = (value = '') => String(value || '').replace(/\s+/g, ' ').trim();

const toLocalDateKey = (date = new Date()) => {
  const safeDate = date instanceof Date && !Number.isNaN(date.getTime()) ? date : new Date();
  const year = safeDate.getFullYear();
  const month = String(safeDate.getMonth() + 1).padStart(2, '0');
  const day = String(safeDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const createTracker = async (tracker = {}, deps = {}) => {
  const firestoreDb = deps.db || db;
  const collectionFn = deps.collection || collection;
  const add = deps.addDoc || addDoc;
  const timestamp = deps.serverTimestamp || serverTimestamp;
  const now = timestamp();
  const payload = buildTrackerPayload({
    ...tracker,
    createdBy: tracker.createdBy || deps.userId || getCurrentUserId(),
    createdAt: tracker.createdAt || now,
    updatedAt: tracker.updatedAt || now,
  });
  const docRef = await add(collectionFn(firestoreDb, TRACKERS_COLLECTION), payload);

  return {
    id: docRef.id,
    ...payload,
  };
};

export const createTrackerEvidence = async (evidence = {}, deps = {}) => {
  const firestoreDb = deps.db || db;
  const collectionFn = deps.collection || collection;
  const add = deps.addDoc || addDoc;
  const timestamp = deps.serverTimestamp || serverTimestamp;
  const now = timestamp();
  const payload = buildTrackerEvidencePayload({
    ...evidence,
    createdBy: evidence.createdBy || deps.userId || getCurrentUserId(),
    createdAt: evidence.createdAt || now,
    updatedAt: evidence.updatedAt || now,
  });
  const docRef = await add(collectionFn(firestoreDb, TRACKER_EVIDENCE_COLLECTION), payload);

  return {
    id: docRef.id,
    ...payload,
  };
};

export const createTrackerWithEvidence = async ({
  tracker = {},
  evidence = {},
  deps = {},
} = {}) => {
  const savedTracker = await createTracker(tracker, deps);
  const savedEvidence = await createTrackerEvidence({
    ...evidence,
    childId: evidence.childId || savedTracker.childId,
    trackerId: savedTracker.id,
  }, deps);

  return {
    tracker: savedTracker,
    evidence: savedEvidence,
  };
};

export const addTrackerEvidenceTimelineEntry = async ({
  tracker = {},
  note = '',
  occurredAt = new Date(),
  childId = '',
  childName = '',
  deps = {},
} = {}) => {
  const trackerId = normalizeText(tracker.id);
  const cleanChildId = normalizeText(childId || tracker.childId);
  const cleanNote = normalizeText(note);
  if (!trackerId) {
    throw new Error('Missing tracker id.');
  }
  if (!cleanChildId) {
    throw new Error('Missing child id.');
  }
  if (!cleanNote) {
    throw new Error('Add a note before saving evidence.');
  }

  const firestoreDb = deps.db || db;
  const collectionFn = deps.collection || collection;
  const docFn = deps.doc || doc;
  const batchFactory = deps.writeBatch || writeBatch;
  const timestamp = deps.serverTimestamp || serverTimestamp;
  const now = timestamp();
  const userId = deps.userId || getCurrentUserId();
  const safeOccurredAt = occurredAt instanceof Date && !Number.isNaN(occurredAt.getTime())
    ? occurredAt
    : new Date();
  const entryDayKey = toLocalDateKey(safeOccurredAt);
  const trackerTitle = normalizeText(tracker.title) || 'Tracker';
  const goalId = normalizeText(tracker.source?.goalId || tracker.source?.id);
  const isProfileGoal = tracker.source?.type === TRACKER_SOURCE_TYPES.PROFILE_GOAL && Boolean(goalId);

  const timelineRef = docFn(collectionFn(firestoreDb, DAILY_LOGS_COLLECTION));
  const timelinePayload = {
    childId: cleanChildId,
    childName: normalizeText(childName || tracker.childName) || null,
    collection: DAILY_LOGS_COLLECTION,
    category: 'log',
    logCategory: 'log',
    type: 'log',
    timelineType: 'log',
    status: 'active',
    title: trackerTitle,
    titlePrefix: 'Daily Log',
    text: cleanNote,
    body: cleanNote,
    note: cleanNote,
    content: cleanNote,
    description: cleanNote,
    summary: cleanNote,
    timestamp: safeOccurredAt,
    timestampUtc: safeOccurredAt,
    localDate: entryDayKey,
    entryDate: safeOccurredAt.toDateString(),
    entryDayKey,
    createdBy: userId,
    authorId: userId,
    createdAt: now,
    updatedAt: now,
    source: {
      type: 'tracker_evidence',
      trackerId,
      trackerTitle,
      ...(isProfileGoal ? {
        goalId,
        goalRelationshipActive: tracker.source?.relationshipActive !== false,
      } : {}),
    },
    metadata: {
      trackerEvidence: {
        trackerId,
        trackerTitle,
        ...(isProfileGoal ? {
          goalId,
          goalRelationshipActive: tracker.source?.relationshipActive !== false,
        } : {}),
      },
    },
    ...(isProfileGoal ? {
      goalId,
      goalRelationshipActive: tracker.source?.relationshipActive !== false,
    } : {}),
  };

  const evidenceRef = docFn(collectionFn(firestoreDb, TRACKER_EVIDENCE_COLLECTION));
  const evidencePayload = buildTrackerEvidencePayload({
    childId: cleanChildId,
    trackerId,
    goalId: isProfileGoal ? goalId : '',
    goalRelationshipActive: isProfileGoal
      ? tracker.source?.relationshipActive !== false
      : undefined,
    summary: cleanNote,
    occurredAt: safeOccurredAt,
    entryDayKey,
    source: {
      type: TRACKER_SOURCE_TYPES.TIMELINE_ENTRY,
      id: timelineRef.id,
      path: `${DAILY_LOGS_COLLECTION}/${timelineRef.id}`,
      text: cleanNote,
    },
    createdBy: userId,
    createdAt: now,
    updatedAt: now,
  });

  const batch = batchFactory(firestoreDb);
  batch.set(timelineRef, timelinePayload);
  batch.set(evidenceRef, evidencePayload);
  await batch.commit();

  return {
    entry: {
      id: timelineRef.id,
      ...timelinePayload,
    },
    evidence: {
      id: evidenceRef.id,
      ...evidencePayload,
    },
  };
};

export const fetchActiveTrackersForChild = async (childId = '', deps = {}) => {
  const cleanChildId = String(childId || '').trim();
  if (!cleanChildId) return [];

  const firestoreDb = deps.db || db;
  const collectionFn = deps.collection || collection;
  const queryFn = deps.query || query;
  const whereFn = deps.where || where;
  const getDocuments = deps.getDocs || getDocs;

  const trackerQuery = queryFn(
    collectionFn(firestoreDb, TRACKERS_COLLECTION),
    whereFn('childId', '==', cleanChildId)
  );
  const snapshot = await getDocuments(trackerQuery);

  return snapshot.docs
    .map((trackerDoc) => ({
      id: trackerDoc.id,
      ...trackerDoc.data(),
    }))
    .filter((tracker) => tracker.status !== TRACKER_STATUSES.ARCHIVED);
};

export const subscribeToActiveTrackersForChild = ({
  childId = '',
  onTrackers = () => {},
  onError = () => {},
} = {}, deps = {}) => {
  const cleanChildId = String(childId || '').trim();
  if (!cleanChildId) {
    onTrackers([]);
    return () => {};
  }

  const firestoreDb = deps.db || db;
  const collectionFn = deps.collection || collection;
  const queryFn = deps.query || query;
  const whereFn = deps.where || where;
  const snapshotFn = deps.onSnapshot || onSnapshot;
  const trackerQuery = queryFn(
    collectionFn(firestoreDb, TRACKERS_COLLECTION),
    whereFn('childId', '==', cleanChildId)
  );

  return snapshotFn(
    trackerQuery,
    (snapshot) => {
      onTrackers(snapshot.docs
        .map((trackerDoc) => ({
          id: trackerDoc.id,
          ...trackerDoc.data(),
        }))
        .filter((tracker) => tracker.status !== TRACKER_STATUSES.ARCHIVED));
    },
    onError
  );
};

export const subscribeToTrackerEvidenceForChild = ({
  childId = '',
  onEvidence = () => {},
  onError = () => {},
} = {}, deps = {}) => {
  const cleanChildId = normalizeText(childId);
  if (!cleanChildId) {
    onEvidence([]);
    return () => {};
  }

  const firestoreDb = deps.db || db;
  const collectionFn = deps.collection || collection;
  const queryFn = deps.query || query;
  const whereFn = deps.where || where;
  const snapshotFn = deps.onSnapshot || onSnapshot;
  const evidenceQuery = queryFn(
    collectionFn(firestoreDb, TRACKER_EVIDENCE_COLLECTION),
    whereFn('childId', '==', cleanChildId)
  );

  return snapshotFn(
    evidenceQuery,
    (snapshot) => onEvidence(snapshot.docs.map((evidenceDoc) => ({
      id: evidenceDoc.id,
      ...evidenceDoc.data(),
    }))),
    onError
  );
};

export const archiveTracker = async (trackerId = '', deps = {}) => {
  const cleanTrackerId = String(trackerId || '').trim();
  if (!cleanTrackerId) {
    throw new Error('Missing tracker id.');
  }

  const firestoreDb = deps.db || db;
  const docFn = deps.doc || doc;
  const update = deps.updateDoc || updateDoc;
  const timestamp = deps.serverTimestamp || serverTimestamp;

  await update(docFn(firestoreDb, TRACKERS_COLLECTION, cleanTrackerId), {
    status: TRACKER_STATUSES.ARCHIVED,
    updatedAt: timestamp(),
    archivedAt: timestamp(),
    archivedBy: deps.userId || getCurrentUserId(),
  });

  return cleanTrackerId;
};

export const fetchTrackerSourceRecord = async (source = {}, deps = {}) => {
  const sourceType = String(source?.type || '').trim();
  const sourceId = String(source?.id || '').trim();
  if (!sourceType || !sourceId) {
    return null;
  }

  const collectionNameByType = {
    careSource: 'careSources',
    timelineEntry: 'dailyLogs',
    knowledgeItem: 'knowledgeItems',
  };
  const collectionName = collectionNameByType[sourceType];
  if (!collectionName) {
    return null;
  }

  const firestoreDb = deps.db || db;
  const docFn = deps.doc || doc;
  const getDocument = deps.getDoc || getDoc;
  const snapshot = await getDocument(docFn(firestoreDb, collectionName, sourceId));
  if (!snapshot?.exists?.()) {
    return null;
  }

  return {
    id: snapshot.id,
    sourceType,
    collection: collectionName,
    ...snapshot.data(),
  };
};

export const findExistingTrackerMatches = async ({
  childId = '',
  candidate = {},
  minimumScore,
  deps = {},
} = {}) => {
  const trackers = await fetchActiveTrackersForChild(childId, deps);
  return findTrackerCandidateMatches({
    trackers,
    candidate,
    minimumScore,
  });
};

export const getBestExistingTrackerMatch = async (options = {}) => (
  getBestTrackerCandidateMatch({
    trackers: await fetchActiveTrackersForChild(options.childId, options.deps || {}),
    candidate: options.candidate,
    minimumScore: options.minimumScore,
  })
);
